<template>
    <div class="responses tw-flex tw-flex-col tw-h-full">
        <div class="head tw-flex tw-gap-4 tw-items-center tw-justify-between">
            <h2>
                {{ Object.keys(this.filters).length ? 'Filtered' : 'All' }} Survey Responses 
                <span v-if="responses.length" style="font-weight: 100">
                    (
                        <template v-if="filteredResponses.length">{{ filteredResponses.length }} / </template>
                        {{ responses.length }}
                    )
                </span>
            </h2>
            <div class="ws-filter ws-filter-select">
                <WSSelect v-model="filterBySource" style="min-width: 150px;" :options="[
                    {id: 'all', name: 'All Sources'},
                    {id: 'public', name: 'Public Survey Page'},
                    ...sources
                ]" text-attr="name" value-attr="id"  />
            </div>
            <div class="ws-button" @click="showingExport = true">Export Results</div>
        </div>
        <div v-if="Object.keys(this.filters).length" class="applied-filters">
            <h4>Active Filters:</h4>
            <div class="tw-flex tw-flex-wrap tw-w-full tw-gap-3">
                <template v-for="(options, id) in filters">
                <div v-for="(option,index) in options" :key="`${id}-${index}`"
                        class="item tw-flex"
                        @click="removeFilter(id, index)">
                    <span>{{ questionsById[id]?.page_name }} = {{ option }}</span>
                    <span class="remove">&times;</span>
                </div>
            </template>
            </div>
        </div>
        <div v-if="hiddenQuestions.length" class="hidden-questions">
            <h4>Hidden Questions:</h4>
            <div class="tw-flex tw-flex-wrap tw-w-full tw-gap-3">
                <div v-for="(id) in hiddenQuestions" :key="id"
                        class="item tw-flex"
                >
                    <span>{{ questionsById[id]?.page_name }}</span>
                    <span class="view" @click="onShowQuestion(questionsById[id])"></span>
                </div>
            </div>
        </div>
        <div v-if="responses.length" class="body tw-flex tw-h-[calc(100%-3.5rem)] tw-gap-4 tw-items-start">
            <div class="responses-table-wrap tw-flex-grow tw-h-full tw-overflow-auto" style="border-right: 2px solid #7DB2BD;">
                <table class="worksends-table table-auto tw-table-fixed" style="margin-bottom: 0; margin-right: 0;">
                    <thead>
                    <tr>
                        <th class="tw-p-2">
                            <span>Time</span>
                        </th>
                        <th class="tw-p-2">
                            <span>Source</span>
                        </th>
                        <th v-for="question in tableQuestions" :key="question.id"
                            class="tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap">
                            <div class="question-filter tw-flex tw-items-center">
                                <div class="name">{{ question.page_name }}</div>
                                <div class="options">
                                    <div class="sort tw-text-center tw-cursor-pointer"
                                        :class="{ 'asc': sort.by === question.id && sort.asc, 'desc': sort.by === question.id && !sort.asc }"
                                        @click="onSort(question)">
                                    </div>
                                    <div class="filter tw-text-center tw-cursor-pointer"
                                        @click="onShowFilter(question)">
                                    </div>
                                    <div class="hide tw-text-center tw-cursor-pointer"
                                        @click="onHideQuestion(question)">
                                    </div>
                                </div>
                            </div>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="response in filteredResponses" :key="response.id"
                        class="hover:tw-bg-gray-100 tw-cursor-pointer"
                        @click="onSelectResponse(response)"
                        :class="{ 'active': selectedResponse && selectedResponse.id === response.id }">
                        <td class="tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-p-1">
                            {{ response.created_at | toDateTime }}
                        </td>
                        <td class="tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-p-1">
                            {{ response.source?.name || 'Public Url' }}
                        </td>
                        <td v-for="question in tableQuestions" :key="question.id"
                            class="tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-p-1">
                            <span v-if="['ranking', 'multiple-choice'].includes(question.type)"
                                :title="response.answers[question.id]?.value?.join(', ')">
                                {{ response.answers[question.id]?.value?.join(', ') }}
                            </span>
                            <span v-else :title="response.answers[question.id]?.value">
                                {{ response.answers[question.id]?.value }}
                            </span>
                        </td>
                    </tr>
                    <tr v-if="!filteredResponses.length">
                        <td colspan="6" class="tw-text-center">
                            <span v-if="Object.keys(filters).length">
                                No responses match your filters
                                <span class="tw-text-blue-300 tw-cursor-pointer tw-ml-1 hover:tw-text-blue-500"
                                    @click="filters = {}">Clear all</span>
                            </span>
                            <span v-if="!Object.keys(filters).length">No responses yet</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="selectedResponse" class="response-preview tw-w-1/3 tw-flex-shrink-0 tw-h-full tw-overflow-auto">
                <div class="response-preview-inner">
                    <a @click="onSelectResponse()" class="close">&times;</a>
                    <h2>Response Details</h2>
                    <div class="tw-h-full tw-overflow-auto">
                        <div class="source">
                            <h5 class="label">Answered At</h5>
                            <div>
                                {{ selectedResponse.created_at | toDateTime }}
                            </div>
                        </div>
                        <div class="source">
                            <h5 class="label">Source</h5>
                            <div>
                                {{ selectedResponse.source?.name || 'Public Url' }}
                            </div>
                            <div class="tw-mt-1 tw-text-sm" v-for="(value, key) in selectedResponse.source_metadata"
                                 :key="`source-meta-${key}`">
                                <span class="tw-font-semibold">{{ key }}:</span>
                                {{ value }}
                            </div>
                        </div>
                        <div class="answers">
                            <h5 class="label">Answers</h5>
                            <div class="answers-box">
                                <div v-for="question in questions" :key="question.id" class="answer">
                                    <div class="question-name tw-font-medium tw-text-sm tw-text-blue-900">{{ question.name ?? question.page_name }}</div>
                                    <div v-html="formatValue(selectedResponse.answers[question.id]?.value,question.type) || '-'"></div>
                                </div>
                            </div>
                        </div>
                        <div class="notes">
                            <h5 class="label">Notes</h5>
                            <div v-for="note in selectedResponse.notes" :key="note.id" class="small-note">
                                <p class="content">{{ note.content }}</p>
                                <span class="meta">{{ note.added_by }} <span>{{ note.created_at | toDateTime }}</span></span>
                            </div>
                            <div class="no-notes" v-if="!selectedResponse.notes">There are currently no notes attached.</div>
                            <div class="add-new">
                                <textarea class="w-full" rows="4" v-model="newNoteText"
                                          placeholder="Create a new note..."></textarea>
                                <div class="ws-button" @click="onAddNote">Add Note</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div v-if="!responsesLoaded" class="worksends-table-loading loadable"><div class="value-loader"></div>Retrieving your survey responses list....</div>
        <div v-else-if="!responses.length" class="worksends-panel">
            <div class="empty-panel-message">
                <h5>This survey does not have any responses yet.</h5> In the survey summary page
                you can get a survey link to share with your respondents, and manage additional
                survey sources to collect results.

                <div class="empty-panel-actions">
                    <router-link class="ws-button ws-button-secondary" :to="{name: 'surveys-details', params: {survey_id: survey.id} }">
                        Survey Summary
                    </router-link>
                </div>
            </div>
        </div>
        
        <!-- <div v-else class="ws-table-simple-sum">This survey has {{ responses.length }} responses.</div> -->
        <modal v-if="showingFilterFor" @close="showingFilterFor = undefined">
            <h3>Filter By <span class="filter-name">{{ showingFilterFor.page_name }}</span></h3>
            <div class="large-form" style="max-height: 50vh; overflow-x: auto">
                <div v-for="(option, index) in filterOptions" :key="index" class="form-line">
                    <div class="form-field form-field-checkbox" :id="`form-field-filter-option-${index}`">
                        <input :id="`form-input-filter-option-${index}`" type="checkbox" :value="option" v-model="selectedOptions[option]" />
                        <label :for="`form-input-filter-option-${index}`" class="checkbox-label" style="display: inline">{{ option }}</label>
                    </div>
                </div>
            </div>
            <div class="form-actions">
                <div class="ws-button ws-button-secondary" @click="showingFilterFor = undefined">Cancel</div>
                <div class="ws-button ws-button-main" :class="{ 'disabled': !Object.keys(selectedOptions).length }" @click="setFilter">Apply Filter</div>
            </div>
        </modal>
        <modal v-if="showingExport" @close="showingExport = false">
            <export-form :selected-filters="{source: filterBySource, ...filters}"
                         :selected-questions="tableQuestions.map(({id}) => id)" @cancel="showingExport = false"/>
        </modal>
    </div>
</template>

<script>
import { indexOf, intersection, keyBy, pickBy, uniq } from 'lodash-es';
import Modal from "@/components/modals/Modal";
import ExportForm from "@/pages/surveys/ExportForm";
import WSSelect from "@/components/elements/WSSelect.vue";
import TableMeta from "@/components/TableMeta.vue";


export default {
    name: 'SurveyDetailsResponses',
    components: {TableMeta, WSSelect, ExportForm, Modal},
    data() {
        return {
            responsesLoaded: false,
            showingFilterFor: undefined,
            showingExport: false,
            selectedOptions: {},
            selectedResponse: undefined,
            filters: {},
            sort: {
                by: undefined,
                asc: true
            },
            hiddenQuestions: [],
            filterBySource: this.$route.query.source || 'all',
            newNoteText: '',
            unsortedResponses: [],
        }
    },
    computed: {
        survey() {
            return this.$store.getters["surveys/selected"];
        },
        sources() {
            return this.$store.getters["surveySources/orderedList"];
        },
        responses() {
            return this.$store.getters["surveyResponses/orderedList"];
        },
        tableQuestions() {
            if (!this.hiddenQuestions.length) return this.questions;
            return this.questions.filter(({ id }) => !this.hiddenQuestions.includes(id));
        },
        questions() {
            return this.$store.getters['surveyPages/orderedList']
                    .reduce((questions, page) => [
                            ...questions,
                            ...page.questions.map(questions => ({...questions, page_name: page.name})),
                        ], [])
        },
        questionsById() {
            return keyBy(this.questions, 'id')
        },
        meta() {
            return this.$store.state.surveyResponses.meta || {};
        },
        filterOptions() {
            if (this.showingFilterFor) {
                let options = this.responses
                        .map(({answers}) => {
                            const answer = answers[this.showingFilterFor.id]?.value;
                            return Array.isArray(answer) ? answer.join(', ') : answer;
                        })
                        .filter(i => i);

                if (['single-choice'].includes(this.showingFilterFor.type)) {
                    options = [...this.showingFilterFor.options, ...options]
                }

                if (this.showingFilterFor.type === 'multiple-choice') {
                    options = [...this.showingFilterFor.options];
                }

                return uniq(options)
            }
            return [];
        },
        filteredResponses() {
            let responses = this.responses;
            let sort = this.sort;
            
            if (!this.unsortedResponses.length) this.unsortedResponses = Array.from(this.responses);
            
            if (this.sort.by) {
                let sortedResponses = [];
                if (this.sort.asc) {
                    sortedResponses = responses.toSorted((a, b) => (a.answers[this.sort.by]?.value > b.answers[this.sort.by]?.value) ? 1 : ((b.answers[this.sort.by]?.value > a.answers[this.sort.by]?.value) ? -1 : 0));
                } else {
                    sortedResponses = responses.toSorted((b, a) => (a.answers[this.sort.by]?.value > b.answers[this.sort.by]?.value) ? 1 : ((b.answers[this.sort.by]?.value > a.answers[this.sort.by]?.value) ? -1 : 0));
                }
                responses = sortedResponses;
            } else responses = this.unsortedResponses;
            
            if (this.filterBySource && this.filterBySource !== 'all') {
                responses = responses.filter(({source_id}) => this.filterBySource === 'public' ? !source_id : source_id == this.filterBySource)
            }
            if (!Object.keys(this.filters).length) {
                return responses;
            }
            
            return responses.filter((response) => {
                return !Object.keys(this.filters).find(id => {
                    if (!response.answers[id]) return true; //no answer
                    if (this.questionsById[id]?.type === 'multiple-choice') {
                        return !intersection(this.filters[id], response.answers[id].value).length
                    }
                    return !this.filters[id].includes(response.answers[id].value.toString())
                });
            })
        },
    },
    watch: {
        filterBySource() {
            this.loadMore(1);
        },
        filters: {
            deep: true,
            handler() {
                // this.loadMore(1);
            },
        },
    },
    async created() {
        if (this.$route.params.response) await this.selectResponse(this.$route.params.response);
        this.setStartingFilter();
        await this.fetchResponses();
    },
    methods: {
        async fetchResponses() {
            await this.$store.dispatch('surveyResponses/getList', {limit: 1000})
            this.responsesLoaded = true;
            this.$store.dispatch('surveyPages/getList', {})
            if (!this.sources.length) {
                this.$store.dispatch('surveySources/getList', {limit: 1000})
            }
        },
        loadMore(page) {
            this.$store.dispatch('surveyResponses/getList', {
                page,
                source: this.filterBySource
            })
        },
        onShowFilter(question) {
            this.selectedOptions = (this.filters[question.id] ?? [])
                    .reduce((options, value) => ({...options, [value]: true}), {});
            this.showingFilterFor = question;
        },
        setStartingFilter() {
            const urlParams = new URL(location.href).searchParams;
            if (urlParams.get('question') && urlParams.get('answer')) {
                this.$set(this.filters, urlParams.get('question'), [urlParams.get('answer')]);
            }
        },
        onSort(question) {
            if (this.sort.by === undefined) {
                this.$set(this.sort, 'by', question.id);
            } else if (this.sort.asc === true) {
                this.$set(this.sort, 'asc', false);
            } else {
                this.$set(this.sort, 'by', undefined);
                this.$set(this.sort, 'asc', true);
            }
        },
        onHideQuestion(question) {
            if (!this.hiddenQuestions.includes(question.id)) this.hiddenQuestions.push(question.id);
        },
        onShowQuestion(question) {
            if (this.hiddenQuestions.includes(question.id)) this.hiddenQuestions.splice(this.hiddenQuestions.indexOf(question.id), 1);
        },
        async onSelectResponse(response) {
            let path = new URL(location.href).pathname.split('?')[0].replace(/\/+$/, '');
            if (response) {
                if (this.selectedResponse) path = path.substring(0, path.lastIndexOf('/'));
                history.pushState({}, null, path + '/' + response.id);
                this.selectedResponse = response;
                this.selectedResponse = await this.$store.dispatch('surveyResponses/getById', response.id);
            } else {
                
                history.pushState({}, null, path.substring(0, path.lastIndexOf('/')));
                this.selectedResponse = null;
            }
        },
        async selectResponse(responseId) {
            this.selectedResponse = await this.$store.dispatch('surveyResponses/getById', responseId);
        },
        setFilter() {
            this.$set(this.filters, this.showingFilterFor.id, Object.keys(pickBy(this.selectedOptions)))
            this.selectedOptions = {};
            this.showingFilterFor = undefined;
        },
        removeFilter(id, index) {
            this.filters[id].splice(index, 1);
            if (!this.filters[id].length) {
                this.$delete(this.filters, id)
            }
        },
        async onAddNote() {
            await this.$store.dispatch('surveyResponses/addNote', {
                id: this.selectedResponse.id,
                content: this.newNoteText
            })
            this.onSelectResponse(this.selectedResponse);
            this.newNoteText = ''
        },
        formatValue(val,type) {
            if (val === undefined || val === null) return false;
            else if (val.constructor === Array) return val.join(', ');
            else if (type === 'website') return '<a href="'+val+'" target="_blank">'+val+'</a>';
            else return val;
        },
    },
}
</script>
